import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import './SheerIdStatusPage.css';
import { Loader } from 'Components/Loader';
import { getEntitlements } from 'State/User/UserActions';
import { StaticLinkButton } from 'Components/Fields/Buttons';
import { SheerIdFinePrint } from 'Components/SheerIdFinePrint';
import {
  SHEER_PAGE_TYPES,
  SHEER_PAGE_ERROR_MESSAGES,
  SHEER_PAGE_TITLES,
  getSheerVerificationId,
} from 'Utils/SheerIdHelpers';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { getPlans, selectPlan } from 'State/Plans/PlansActions';
import { SHEER_MILITARY_PACKAGE_ID } from 'Utils/Constants';
import RestartVerificationButton from '../components/RestartVerificationButton';
import { getSheerIdType } from 'Utils/SheerIdHelpers';
import ExitVerificationButton from '../components/ExitVerificationButton';

export const SheerIdStatusPage = ({ pageType, setPage, setTitle, verificationId, partner, program, errorIds = [] }) => {
  const dispatch = useDispatch();
  const {
    defaultConfig: { rootDomain },
  } = useSelector((s) => s.appConfig);
  const { subscriptionId, redirectUrl } = useSelector((s) => s.integrations.bango);
  const { isAuthenticated } = useSelector((s) => s.user);
  const plans = useSelector((s) => s.plans);
  const { allPlans, getPlansSuccess, isFetchingPlans } = plans;
  const routes = useSelector((s) => s.appConfig.routes);
  const { hasSubscription } = useSelector((s) => s.subscription);
  const [packageId, setPackageId] = useState('');
  const [savedRedirect, setSavedRedirect] = useState(redirectUrl);
  const [isSuccess, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const navigate = useNavigate();
  const sheerIdType = getSheerIdType();
  const { pathname } = useLocation();

  let path = routes.signupCreateAccount;
  // partner is a prop passed with the step that tells which kind of user someone is fox = nonbango
  if (isAuthenticated && partner === 'Fox') {
    path = routes.payment;
  }

  const isErrorPage = pageType === SHEER_PAGE_TYPES.ERROR;

  let errorMessage = SHEER_PAGE_ERROR_MESSAGES.unknownError;

  if (errorIds.length > 0) {
    errorMessage = SHEER_PAGE_ERROR_MESSAGES[errorIds[0]] || errorMessage;
  }

  const text = {
    [SHEER_PAGE_TYPES.SUCCESS]: {
      BODY: savedRedirect
        ? 'Your account has been successfully activated with Verizon +play subscription. Enjoy Fox Nation!'
        : 'Continue to the next step to create your Fox Nation account and checkout with your military discount.',
      PRIMARY_BUTTON: savedRedirect ? 'Continue to Verizon' : 'Continue to FOX Nation',
      PRIMARY_PATH: savedRedirect || path || rootDomain,
    },
    [SHEER_PAGE_TYPES.ERROR]: {
      BODY: errorMessage,
      PRIMARY_BUTTON: savedRedirect ? 'Back to Verizon' : 'Back to FOX Nation',
      PRIMARY_PATH: savedRedirect || rootDomain,
    },
    [SHEER_PAGE_TYPES.INELIGIBLE]: {
      BODY: savedRedirect
        ? 'It looks like you are already a valued Fox Nation subscriber. If you would like to purchase an additional subscription through Verizon +play, please use a new email address.'
        : 'It looks like you are already a valued Fox Nation subscriber. If you would like to purchase an additional subscription, please use a new email address.',
      PRIMARY_BUTTON: savedRedirect ? 'Continue to Verizon' : 'Continue to FOX Nation',
      PRIMARY_PATH: savedRedirect || rootDomain,
    },
  };

  useEffect(() => {
    const activate = () => {
      try {
        dispatch(getEntitlements());
        setLoading(false);
        setTitle(SHEER_PAGE_TITLES[SHEER_PAGE_TYPES.SUCCESS]);
        trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_COMPLETED, {
          sheer_id: verificationId,
          partner: subscriptionId ? 'bango' : 'fox',
          program,
        });
      } catch (error) {
        setPage(SHEER_PAGE_TYPES.ERROR, { verificationId, ...error });
        setLoading(false);
      }
    };

    const selectPlanMilitaryPayment = async () => {
      localStorage.setItem('sheerVerificationId', verificationId);
      await dispatch(getPlans());
    };

    if (pageType === SHEER_PAGE_TYPES.SUCCESS && !isSuccess && savedRedirect) {
      // This is to only call activate once on status change
      setSuccess(true);
      activate();
    }
    if (pageType === SHEER_PAGE_TYPES.SUCCESS && !isSuccess && !savedRedirect) {
      // This is nonbango user success page
      setSuccess(true);
      trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_COMPLETED, {
        sheer_id: verificationId,
        partner: subscriptionId ? 'bango' : 'fox',
        program,
      });
      selectPlanMilitaryPayment();
    }
    if (pageType === SHEER_PAGE_TYPES.ERROR) {
      setTitle(SHEER_PAGE_TITLES[SHEER_PAGE_TYPES.ERROR]);
      trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_REJECTED);
      setLoading(false);
    }

    if (pageType === SHEER_PAGE_TYPES.INELIGIBLE) {
      setTitle(SHEER_PAGE_TITLES[SHEER_PAGE_TYPES.INELIGIBLE]);
      trackEvent(TRACKING_ENUMS.EVENT_STATUS_VERIFICATION_REJECTED);
      setLoading(false);
    }
  }, [
    setLoading,
    savedRedirect,
    pageType,
    setTitle,
    dispatch,
    setSavedRedirect,
    setPage,
    verificationId,
    isSuccess,
    navigate,
    path,
    subscriptionId,
    program,
  ]);

  useEffect(() => {
    // This is to navigate to the payment page for non-bango users with the preselected id
    const hasSheerIdPackages = !isFetchingPlans && getPlansSuccess && allPlans.length && !initialLoad;
    const setPackages = async () => {
      if (allPlans.length && getSheerVerificationId()) {
        if (allPlans.find((plan) => plan.packageID === SHEER_MILITARY_PACKAGE_ID)) {
          await dispatch(selectPlan(SHEER_MILITARY_PACKAGE_ID));
          setPackageId(SHEER_MILITARY_PACKAGE_ID);
        }
      }
      if (setInitialLoad) setInitialLoad(false);
    };

    if (partner === 'Fox') {
      if (hasSheerIdPackages && packageId) {
        if (allPlans.find((plan) => plan.packageID === packageId)) {
          navigate(`${path}/${packageId}`);
        }
      } else if (hasSheerIdPackages) {
        navigate(path, { state: { from: pathname } });
      }

      setPackages();
    }
  }, [
    path,
    allPlans,
    packageId,
    getPlansSuccess,
    isFetchingPlans,
    navigate,
    dispatch,
    plans,
    initialLoad,
    partner,
    pathname,
  ]);

  if (loading) return <Loader />;
  return (
    <div className="sheerIdStatusPage">
      <div className="sheerIdStatusMainText">{text[pageType].BODY}</div>
      {isErrorPage && <RestartVerificationButton setPage={setPage} />}
      {isErrorPage && <ExitVerificationButton routes={routes} hasSubscription={hasSubscription} />}
      {!!text[pageType].PRIMARY_BUTTON && !!text[pageType].PRIMARY_PATH && (
        <StaticLinkButton
          className={`sheerIdStatusPrimaryButton ${isErrorPage ? 'hasFinePrint' : ''}`}
          path={text[pageType].PRIMARY_PATH}
          text={text[pageType].PRIMARY_BUTTON}
        />
      )}
      {isErrorPage && <SheerIdFinePrint isReview />}
    </div>
  );
};
